import React, { useState, useEffect } from 'react';
import ReportcenterFilter from './ReportcenterFilter';
import { Block } from '@upsales/components';
import bemClass from '@upsales/components/Utils/bemClass';
import T from 'Components/Helpers/translate';
import './ReportcenterFilterClient.scss';
import ReportcenterFilterRow from './ReportcenterFilterRow';
import { removeItem } from 'Store/helpers/array';
import Client from 'App/resources/Client';
import logError from 'App/babel/helpers/logError';
import { RCDashboardFilter } from 'Resources/ReportDashboard';
import ClientModel from '../../../../resources/Model/Client';
import ReportcenterFilterSearchbar from './ReportcenterFilterSearchbar';

export const renderSelected = (values: RCDashboardFilter, returnCount = true, clients: ClientModel[] = []) => {
	let res = `${T('default.clients')}: `;

	if (!returnCount) {
		const clientNames = clients
			.filter(client => {
				return values?.Client?.value?.includes?.(client.id);
			})
			.map(client => client.name);
		res += clientNames.join(', ');
		return res;
	}

	const totalCount = values?.Client?.value?.length ?? 0;
	if (totalCount >= 1) {
		res += `${totalCount} ${(values.Client!.comparison === 'eq'
			? T('default.selected')
			: T('default.excluded', { count: totalCount })
		).toLowerCase()}`;
	}
	return res;
};

interface Props {
	onChange: (type: keyof RCDashboardFilter, values: number[], comparison: string) => void;
	values: RCDashboardFilter;
	updateFilterHeight: () => void;
}

const ReportcenterFilterClient = ({ onChange, ...props }: Props) => {
	const classes = new bemClass('ReportcenterFilterClient');
	const [searchStr, setSearchStr] = useState('');
	const [clients, setClients] = useState<ClientModel[]>([]);
	const [selectedClients, setSelectedClients] = useState<ClientModel[]>([]);
	const value = props.values?.Client?.value || [];
	const comparison = props.values?.Client?.comparison ?? 'eq';
	const isExclude = comparison === 'ne';
	const array = [...selectedClients, ...clients.filter(c => !selectedClients.some(sc => sc.id === c.id))];

	const search = (query: string) => {
		Client.find({ name: `src:${query}`, isExternal: 0, sort: 'name', limit: 20 })
			.then(res => {
				setClients(res.data);
			})
			.catch(err => logError(err));
	};

	const updateSelectedClients = () => {
		if (value.length) {
			Client.find({ id: value, sort: 'name', isExternal: 0 })
				.then(res => setSelectedClients(res.data))
				.catch(err => logError(err));
		} else {
			setSelectedClients([]);
		}
	};

	useEffect(() => {
		search(searchStr);
		updateSelectedClients();
	}, []);

	useEffect(() => {
		updateSelectedClients();
	}, [value]);

	useEffect(() => {
		const timeout = setTimeout(() => search(searchStr), 300);
		return () => clearTimeout(timeout);
	}, [searchStr]);

	return (
		<ReportcenterFilter
			className={classes.b()}
			renderSelected={() => renderSelected(props.values)}
			getSelectedNames={() => array.filter(u => props.values?.Client?.value?.includes(u.id)).map(u => u.name)}
			icon="home"
			placeholder={`${T('default.clients')}: ${T('reportcenter.filter.notActive')}`}
			value={value}
			resetFilter={() => {
				onChange('Client', [], 'eq');
			}}
			{...props}
		>
			<Block space="ptm prm pbm plm" backgroundColor="white" className={classes.elem('list-select').b()}>
				<Block space="mtm">
					<ReportcenterFilterSearchbar<number[]>
						setSearchStr={value => setSearchStr(value)}
						searchStr={searchStr}
						placeholder={T('activity.searchClients')}
						isExclude={isExclude}
						onChange={onChange}
						field="Client"
						value={value}
					/>
				</Block>
			</Block>
			<ReportcenterFilterRow
				key={0}
				onClick={() => {
					onChange('Client', [], 'eq');
				}}
				selected={!value.length}
				title={T('filters.noFilter')}
			/>
			{array.map(row => (
				<ReportcenterFilterRow
					key={row.id}
					onClick={() => {
						const i = value.indexOf(row.id);
						onChange('Client', i !== -1 ? removeItem(value, i) : [...value, row.id], comparison);
					}}
					selected={value.indexOf(row.id) !== -1}
					title={row.name}
				/>
			))}
		</ReportcenterFilter>
	);
};

export default ReportcenterFilterClient;
